import { useEffect, useState } from "react";
import { ArrowLeft, Plus, UserPlus, Info, Check, Copy, Eye, ChevronRight, Search, ChevronLeft, ChevronRight as ChevronRightIcon } from "lucide-react";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import { Textarea } from "@/ui/components/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/primitives/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/ui/components/primitives/dialog";
import { Alert, AlertDescription } from "@/ui/components/primitives/alert";
import { Badge } from "@/ui/components/primitives/badge";
import { useAuth } from "@/context/AuthContext";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";
import { createPayout, listBeneficiaries, createBeneficiary, listPayouts, getWallets, getMerchantFees } from "@/services/api/services/payouts";
import { PayoutDetailModal } from "../components/payout-detail-modal";

interface CreatePayoutPageProps {
  onBack: () => void;
}

export function CreatePayoutPage({ onBack }: CreatePayoutPageProps) {
  const [amount, setAmount] = useState("");
  const [calculatedFee, setCalculatedFee] = useState(0);
  const { user } = useAuth();
  const [currency, setCurrency] = useState<"INR" | "USDT">("INR");
  const [mode, setMode] = useState("imps");
  const [purpose, setPurpose] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any>(null);
  const [copiedRef, setCopiedRef] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const [beneficiaryMode, setBeneficiaryMode] = useState<"direct" | "saved">("direct");
  const [benName, setBenName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [network, setNetwork] = useState("TRC20");

  const [beneficiaries, setBeneficiaries] = useState<any[]>([]);
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<string>("");
  const [benEmail, setBenEmail] = useState("");
  const [benPhone, setBenPhone] = useState("");
  const [addrLine1, setAddrLine1] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrCountry, setAddrCountry] = useState("");
  const [addrPostal, setAddrPostal] = useState("");
  const [benDialogOpen, setBenDialogOpen] = useState(false);

  const [walletAvailable, setWalletAvailable] = useState<number | null>(null);
  const [wallets, setWallets] = useState<any[]>([]);
  const [selectedWalletId, setSelectedWalletId] = useState<string>("");
  const [fees, setFees] = useState<{ percentage: number; fixed: number } | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);

  const [payouts, setPayouts] = useState<any[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsError, setPayoutsError] = useState<string | null>(null);

  // Recent Payouts additional states
  const [selectedPayout, setSelectedPayout] = useState<any>(null);
  const [payoutDetailModalOpen, setPayoutDetailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [totalCount, setTotalCount] = useState(0);

  const handleAmountChange = (value: string) => {
    setAmount(value);
  };

  const onCreate = async () => {
    setError("");
    setFieldErrors({});
    setLoading(true);
    try {
      const payload: any = {
        amount: parseFloat(amount || "0"),
        currency,
        purpose,
        mode: currency === "INR" ? mode.toUpperCase() : "CRYPTO",
        reference,
        notes,
        fundingWalletId: selectedWalletId || undefined,
      };
      if (beneficiaryMode === "saved" && selectedBeneficiaryId) {
        payload.beneficiaryId = selectedBeneficiaryId;
      } else {
        payload.beneficiary = currency === "INR"
          ? { name: benName, accountNumber, ifscCode, bankName, accountType: "BANK_ACCOUNT" }
          : { name: benName, walletAddress, network: "TRC20", accountType: "CRYPTO" };
      }
      const data = await createPayout(payload);
      setResult(data);
      showSuccessToast("Payout created", data.id || "");
    } catch (e: any) {
      const apiErr = e?.detail?.error || e?.error || e;
      const code = apiErr?.code || apiErr?.name || "ERROR";
      let msg = apiErr?.message || "Failed to create payout";
      if (String(code).toUpperCase() === "INSUFFICIENT_BALANCE") {
        msg = "Insufficient balance in selected wallet. Reduce amount or choose another wallet.";
      }
      const details = Array.isArray(apiErr?.details) ? apiErr.details : [];
      if (details.length) {
        const map: Record<string, string> = {};
        details.forEach((d: any) => { if (d?.field && d?.message) map[d.field] = d.message; });
        setFieldErrors(map);
        const joined = details.map((d: any) => `${d.field}: ${d.message}`).join(", ");
        msg = `${msg} • ${joined}`;
      }
      const req = apiErr?.requestId ? ` • Ref: ${apiErr.requestId}` : "";
      const pretty = `${code}: ${msg}${req}`;
      setError(pretty);
      showErrorToast("Payout failed", pretty);
    } finally {
      setLoading(false);
    }
  };

  const loadBeneficiaries = async () => {
    try {
      const list = await listBeneficiaries({ page: 1, limit: 50 });
      setBeneficiaries(list || []);
    } catch {}
  };

  useEffect(() => {
    loadBeneficiaries();
    (async () => {
      setLoadingMeta(true);
      setMetaError(null);
      try {
        const wlts = await getWallets();
        setWallets(wlts || []);
        const merchantWallet = Array.isArray(wlts) ? wlts.find((w: any) => w.currency === currency) || wlts[0] : null;
        setSelectedWalletId(merchantWallet?.id || "");
        setWalletAvailable(merchantWallet?.availableBalance ?? null);
        const mId = user?.merchantId || merchantWallet?.merchantId;
        if (mId) {
          const feeCfg = await getMerchantFees(mId);
          setFees(feeCfg?.payout || null);
        }
      } catch (e: any) {
        setMetaError("Failed to load wallet/fees");
      } finally {
        setLoadingMeta(false);
      }
    })();
    (async () => {
      setPayoutsLoading(true);
      setPayoutsError(null);
      try {
        const res = await listPayouts({ page: 1, limit: 20, status: ["SUCCESS", "PENDING"], dateFrom: "2025-01-01", dateTo: "2025-12-31" });
        setPayouts(res.data || []);
      } catch (e: any) {
        setPayoutsError("Failed to load recent payouts");
      } finally {
        setPayoutsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const numAmount = parseFloat(amount || "0") || 0;
    if (fees) {
      if (numAmount <= 0) {
        setCalculatedFee(0);
      } else {
        const fixedFee = fees.fixed || 0;
        const percentageFee = numAmount * ((fees.pct || fees.percentage || 0) / 100);
        const fee = percentageFee + fixedFee;
        setCalculatedFee(isNaN(fee) ? 0 : fee);
      }
    } else {
      setCalculatedFee(0);
    }
  }, [amount, fees]);

  // Load recent payouts
  const loadRecentPayouts = async () => {
    setPayoutsLoading(true);
    setPayoutsError(null);
    try {
      const result = await listPayouts({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
        status: statusFilter !== "ALL" ? statusFilter : undefined,
      });
      setPayouts(result.data || []);
      setTotalPages(result.pagination?.pages || 1);
      setTotalCount(result.pagination?.total || 0);
    } catch (error: any) {
      console.error("Failed to load recent payouts:", error);
      setPayoutsError("Failed to load recent payouts");
      setPayouts([]);
    } finally {
      setPayoutsLoading(false);
    }
  };

  useEffect(() => {
    loadRecentPayouts();
  }, [currentPage, pageSize, searchQuery, statusFilter]);

  const onSaveBeneficiary = async () => {
    setError("");
    setLoading(true);
    try {
      const payload = {
        name: benName,
        type: "individual" as const,
        email: benEmail || null,
        phone: benPhone || null,
        accountDetails: {
          accountHolderName: benName,
          accountNumber,
          ifscCode,
          bankName,
          accountType: "savings",
        },
        address: {
          line1: addrLine1 || "",
          city: addrCity || "",
          state: addrState || "",
          country: addrCountry || "",
          postalCode: addrPostal || "",
        },
      };
      const created = await createBeneficiary(payload as any);
      showSuccessToast("Beneficiary saved", created.id || "");
      await loadBeneficiaries();
      setBeneficiaryMode("saved");
      setSelectedBeneficiaryId(created.id);
    } catch (e) {
      setError("Failed to save beneficiary");
      showErrorToast("Save failed", "Please check details and try again");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for Recent Payouts
  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "success":
        return {
          backgroundColor: "#10b981",
          color: "white",
          borderColor: "#10b981"
        };
      case "pending":
        return {
          backgroundColor: "#eab308",
          color: "white",
          borderColor: "#eab308"
        };
      case "processing":
        return {
          backgroundColor: "#3b82f6",
          color: "white",
          borderColor: "#3b82f6"
        };
      case "queued":
        return {
          backgroundColor: "#3b82f6",
          color: "white",
          borderColor: "#3b82f6"
        };
      case "failed":
        return {
          backgroundColor: "#ef4444",
          color: "white",
          borderColor: "#ef4444"
        };
      case "cancelled":
        return {
          backgroundColor: "#6b7280",
          color: "white",
          borderColor: "#6b7280"
        };
      default:
        return {
          backgroundColor: "#6b7280",
          color: "white",
          borderColor: "#6b7280"
        };
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const handleViewPayoutDetails = (payout: any) => {
    setSelectedPayout(payout);
    setPayoutDetailModalOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden min-w-0">
      {/* Header */}
      <div>
        <Button variant="ghost" className="mb-4 -ml-3" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Button>
        <h1>Create Payout</h1>
        <p className="text-muted-foreground mt-1">
          Transfer funds to beneficiary accounts
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card className="lg:col-span-2 p-6">
          <div className="space-y-6">
            {error && (
              <Alert>
                <AlertDescription className="text-destructive">{error}</AlertDescription>
              </Alert>
            )}
            {/* Currency */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <div className="flex gap-2">
                <Button variant={currency === "INR" ? "default" : "outline"} size="sm" onClick={() => setCurrency("INR")}>INR</Button>
                <Button variant={currency === "USDT" ? "default" : "outline"} size="sm" onClick={() => setCurrency("USDT")}>Crypto (USDT • TRC20)</Button>
              </div>
            </div>

            {/* Amount Section - Moved to Top */}
            <div className="space-y-3">
              <Label htmlFor="payout-amount" className="text-lg font-semibold">Payout Amount *</Label>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-muted-foreground pointer-events-none ${currency === "USDT" ? "w-16" : "w-8"}`}>{currency === "INR" ? "₹" : "USDT"}</span>
                <Input
                  id="payout-amount"
                  type="number"
                  placeholder="10000"
                  className="text-2xl font-bold h-16 px-4"
                  style={{ paddingLeft: currency === "USDT" ? 80 : 40 }}
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                />
              </div>
            </div>

            {/* Beneficiary Mode */}
            <div className="space-y-2">
              <Label>Beneficiary *</Label>
              <div className="flex gap-2">
                <Button variant={beneficiaryMode === "direct" ? "default" : "outline"} size="sm" onClick={() => setBeneficiaryMode("direct")}>Send without beneficiary</Button>
                <Button variant={beneficiaryMode === "saved" ? "default" : "outline"} size="sm" onClick={() => setBeneficiaryMode("saved")}>Saved</Button>
                <Button variant="outline" size="icon" onClick={() => setBenDialogOpen(true)} aria-label="Add Beneficiary">
                  <UserPlus className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
              {beneficiaryMode === "saved" ? (
                <div className="flex gap-2 pt-2">
                  <Select value={selectedBeneficiaryId} onValueChange={setSelectedBeneficiaryId}>
                    <SelectTrigger id="beneficiary" className="flex-1">
                      <SelectValue placeholder="Choose from saved beneficiaries" />
                    </SelectTrigger>
                    <SelectContent>
                      {beneficiaries.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name} {b.phone ? `• ${b.phone}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label>Account Holder / Name</Label>
                    <Input value={benName} onChange={(e) => setBenName(e.target.value)} />
                  </div>
                  {currency === "INR" ? (
                    <>
                      <div className="space-y-2">
                        <Label>Account Number</Label>
                        <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>IFSC Code</Label>
                        <Input value={ifscCode} onChange={(e) => setIfscCode(e.target.value)} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Wallet Address</Label>
                        <Input value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>Network</Label>
                        <Select defaultValue={network.toLowerCase()} onValueChange={(v) => setNetwork(v.toUpperCase())}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="trc20">TRC20</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Beneficiary Details - Only show when details are entered */}
            {((beneficiaryMode === "saved" && selectedBeneficiaryId) ||
              (beneficiaryMode === "direct" && benName &&
                (currency === "USDT" ? walletAddress : accountNumber))) && (
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <h4 className="font-medium">Beneficiary Details</h4>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  {beneficiaryMode === "saved" ? (
                    <>
                      <div>
                        <p className="text-muted-foreground">Name</p>
                        <p className="font-medium">{(beneficiaries.find((b) => b.id === selectedBeneficiaryId)?.name) || ""}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Phone</p>
                        <p className="font-medium">{(beneficiaries.find((b) => b.id === selectedBeneficiaryId)?.phone) || ""}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-muted-foreground">Address</p>
                        <p className="font-medium">{(beneficiaries.find((b) => b.id === selectedBeneficiaryId)?.address?.line1) || ""}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-muted-foreground">Account Holder</p>
                        <p className="font-medium">{benName}</p>
                      </div>
                      {currency === "INR" ? (
                        <>
                          <div>
                            <p className="text-muted-foreground">Account Number</p>
                            <p className="font-medium font-mono">{accountNumber ? `${accountNumber.slice(0, 2)}****${accountNumber.slice(-2)}` : ""}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Bank</p>
                            <p className="font-medium">{bankName}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">IFSC Code</p>
                            <p className="font-medium font-mono">{ifscCode}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="text-muted-foreground">Wallet</p>
                            <p className="font-medium font-mono break-all">{walletAddress}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Network</p>
                            <p className="font-medium">{network}</p>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

  
            <div className="space-y-2">
              <Label htmlFor="fund-source">Fund Source</Label>
              {wallets.length > 0 ? (
                <Select value={selectedWalletId} onValueChange={(id) => { setSelectedWalletId(id); const w = wallets.find((x: any) => x.id === id); setWalletAvailable(w?.availableBalance ?? null); }}>
                  <SelectTrigger id="fund-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {wallets.map((w: any) => (
                      <SelectItem key={w.id} value={w.id}>
                        {(w.walletType === "MERCHANT" ? "Main Wallet" : w.walletType)} • {w.currency} (Available: {w.currency === "INR" ? `₹${w.availableBalance}` : `${w.availableBalance} ${w.currency}`})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select disabled>
                  <SelectTrigger id="fund-source">
                    <SelectValue placeholder="No wallets found" />
                  </SelectTrigger>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="purpose">Purpose / Note</Label>
              <Textarea id="purpose" placeholder="Vendor payment for services (optional)" rows={3} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
            </div>

  
            <Alert>
              <Info className="h-4 w-4" strokeWidth={1.5} />
              <AlertDescription>
                All payouts are subject to verification. Instant transfers (IMPS)
                are processed within 5 minutes during business hours.
              </AlertDescription>
            </Alert>

            <div className="pt-4">
              <Button className="w-full" onClick={onCreate} disabled={loading}>{loading ? "Creating..." : "Create Payout"}</Button>
            </div>
          </div>
        </Card>

        {/* Summary & Fee Calculation */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="mb-4">Payout Summary</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b">
                <span className="text-muted-foreground">Payout Amount</span>
                <span className="font-semibold">{currency === "INR" ? `₹${amount || "0.00"}` : `${amount || "0.00"} USDT`}</span>
              </div>
              {(() => { const amt = parseFloat(amount || "0"); const selectedWallet = wallets.find((w: any) => w.id === selectedWalletId); const wc = selectedWallet?.currency; if (amt > 0 && currency === "USDT" && wc === "INR") { return (<div className="flex items-center justify-between pb-3 border-b"><span className="text-muted-foreground">Conversion</span><span className="font-semibold">{`₹${(amt * 99).toFixed(2)} (99 INR = 1 USDT)`}</span></div>); } return null; })()}
              {(() => { const amt = parseFloat(amount || "0"); if (!(amt > 0 && fees)) return null; const fixedFee = fees.fixed || 0; const percentage = (fees.pct || fees.percentage || 0) / 100; if (currency === "INR") { const feeInInr = (amt * percentage) + fixedFee; return (<div className="flex items-center justify-between pb-3 border-b"><span className="text-muted-foreground">Processing Fee</span><span className="font-semibold text-destructive">{`- ₹${feeInInr.toFixed(2)}`}</span></div>); } const percentUsdt = amt * percentage; return (<div className="flex items-center justify-between pb-3 border-b"><span className="text-muted-foreground">Processing Fee</span><span className="font-semibold text-destructive">{`- ${percentUsdt.toFixed(2)} USDT + ₹${fixedFee.toFixed(2)}`}</span></div>); })()}
              <div className="flex items-center justify-between pt-2">
                <span className="font-medium">Net Amount (debited)</span>
                <span className="text-xl font-semibold">{(() => { const amt = parseFloat(amount || "0") || 0; if (!fees) return currency === "INR" ? `₹${amt.toFixed(2)}` : `${amt.toFixed(2)} ${currency}`; const selectedWallet = wallets.find((w: any) => w.id === selectedWalletId); const wc = selectedWallet?.currency; const fixedFee = fees.fixed || 0; const percentage = (fees.pct || fees.percentage || 0) / 100; if (wc === "INR") { const amtInWallet = currency === "USDT" ? amt * 99 : amt; const percentPartInWallet = currency === "USDT" ? (amt * percentage) * 99 : amt * percentage; const net = amtInWallet + percentPartInWallet + fixedFee; return `₹${net.toFixed(2)}`; } if (wc) { const percentUsdt = amt * percentage; const net = amt + percentUsdt; return `${net.toFixed(2)} ${wc}`; } return currency === "INR" ? `₹${amt.toFixed(2)}` : `${amt.toFixed(2)} ${currency}`; })()}</span>
              </div>

              <div className="pt-4 border-t">
                {loadingMeta ? (
                  <p className="text-sm text-muted-foreground">Loading wallet/fees…</p>
                ) : metaError ? (
                  <Alert>
                    <AlertDescription className="text-destructive">{metaError}</AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Available Balance</span>
                      <span className="font-semibold">{(() => { const selectedWallet = wallets.find((w: any) => w.id === selectedWalletId); const wc = selectedWallet?.currency; if (wc === "INR") return `₹${walletAvailable ?? "-"}`; if (wc) return `${walletAvailable ?? "-"} ${wc}`; return `${walletAvailable ?? "-"}`; })()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-muted-foreground">Balance After</span>
                      <span className="font-semibold text-success">{(() => { const amt = parseFloat(amount || "0") || 0; const selectedWallet = wallets.find((w: any) => w.id === selectedWalletId); const wc = selectedWallet?.currency; let netDebit = 0; if (!fees) { netDebit = wc === "INR" && currency === "USDT" ? amt * 99 : amt; } else { const fixedFee = fees.fixed || 0; const percentage = (fees.pct || fees.percentage || 0) / 100; if (wc === "INR") { const amtInWallet = currency === "USDT" ? amt * 99 : amt; const percentPartInWallet = currency === "USDT" ? (amt * percentage) * 99 : amt * percentage; netDebit = amtInWallet + percentPartInWallet + fixedFee; } else if (wc) { const percentUsdt = amt * percentage; netDebit = amt + percentUsdt; } } const after = walletAvailable != null ? (walletAvailable - netDebit).toFixed(2) : "-"; if (wc === "INR") return `₹${after}`; if (wc) return `${after} ${wc}`; return after; })()}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>

          {result && (
            <Card className="p-6">
              <h3 className="mb-4">Payout Created</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Payout ID</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{result.id}</span>
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(result.id); setCopiedId(true); setTimeout(() => setCopiedId(false), 1500); }}>
                      {copiedId ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Reference</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{result.reference}</span>
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(result.reference || ""); setCopiedRef(true); setTimeout(() => setCopiedRef(false), 1500); }}>
                      {copiedRef ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="font-semibold">{result.status}</span>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Recent Payouts Section - Moved Below Form */}
      <Card className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">Recent Payouts</h3>
              <p className="text-sm text-muted-foreground">{payouts?.length || 0} total payouts</p>
            </div>

            {/* Filters */}
            <div className="flex flex-col lg:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by reference, beneficiary..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>
              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40 h-10">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Status</SelectItem>
                    <SelectItem value="SUCCESS">Success</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="w-20 h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {payoutsLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : payoutsError ? (
              <Alert>
                <AlertDescription>{payoutsError}</AlertDescription>
              </Alert>
            ) : payouts?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No recent payouts</p>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Reference ID</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Amount</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Fees</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Beneficiary</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Mode</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Status</th>
                          <th className="text-right py-3 px-2 font-medium text-muted-foreground">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.map((payout) => (
                          <tr
                            key={payout.id}
                            className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => handleViewPayoutDetails(payout)}
                          >
                            <td className="py-3 px-2">
                              <span className="font-mono text-xs">{payout.reference || payout.id.slice(0, 8)}...</span>
                            </td>
                            <td className="py-3 px-2">
                              <span className="font-semibold">
                                {payout.currency === "INR" ? `₹${payout.amount}` : `${payout.amount} ${payout.currency}`}
                              </span>
                            </td>
                            <td className="py-3 px-2">
                              <span className="text-sm text-muted-foreground">
                                {payout.currency === "INR" ? `₹${payout.fees || 0}` : `${payout.fees || 0} ${payout.currency}`}
                              </span>
                            </td>
                            <td className="py-3 px-2">
                              <p className="font-medium">{payout.beneficiary?.name}</p>
                            </td>
                            <td className="py-3 px-2">
                              <span className="text-sm text-muted-foreground uppercase">{payout.mode || 'IMPS'}</span>
                            </td>
                            <td className="py-3 px-2">
                              <Badge
                                className="text-xs"
                                style={getStatusColor(payout.status)}
                              >
                                {payout.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className="text-xs text-muted-foreground">
                                {formatDate(payout.updatedAt || payout.createdAt)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden">
                  {payouts.map((payout, index) => (
                    <div key={payout.id}>
                      <div
                        className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer mb-4"
                        onClick={() => handleViewPayoutDetails(payout)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-lg">
                                {payout.currency === "INR" ? `₹${payout.amount}` : `${payout.amount} ${payout.currency}`}
                              </span>
                              <Badge
                                className="text-xs"
                                style={getStatusColor(payout.status)}
                              >
                                {payout.status}
                              </Badge>
                            </div>
                            <span className="font-mono text-xs text-muted-foreground">
                              {payout.reference || payout.id.slice(0, 8)}...
                            </span>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground mt-1" />
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Beneficiary:</span>
                            <span className="font-medium">{payout.beneficiary?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Mode:</span>
                            <span className="text-xs">{payout.mode}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Date:</span>
                            <span className="text-xs">{formatDate(payout.updatedAt || payout.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      {/* Add separator after each card except the last one */}
                      {index < payouts.length - 1 && (
                        <div
                          className="w-full h-px mb-4 bg-border"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 pt-4 border-t">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {payouts.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to{' '}
                    {Math.min(currentPage * pageSize, totalCount)} of {totalCount} payouts
                  </div>
                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || payoutsLoading}
                      className="hidden sm:flex items-center"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || payoutsLoading}
                      className="sm:hidden"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {/* Page numbers - simplified for mobile */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages <= 20 ? totalPages : 3, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage <= 2) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 1) {
                          pageNum = totalPages - 2 + i;
                        } else {
                          pageNum = currentPage - 1 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={payoutsLoading}
                            className="w-8 h-8 p-0 text-xs"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      {totalPages > 3 && (
                        <>
                          {currentPage < totalPages - 1 && (
                            <>
                              <span className="px-1 text-xs text-muted-foreground">...</span>
                              <Button
                                variant={currentPage === totalPages ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={payoutsLoading}
                                className="w-8 h-8 p-0 text-xs"
                              >
                                {totalPages}
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || payoutsLoading}
                      className="hidden sm:flex items-center"
                    >
                      Next
                      <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || payoutsLoading}
                      className="sm:hidden"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>

      <Dialog open={benDialogOpen} onOpenChange={setBenDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Beneficiary</DialogTitle>
            <DialogDescription>Enter account details to save for future payouts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Holder / Name</Label>
                <Input value={benName} onChange={(e) => setBenName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Account Number</Label>
                <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>IFSC Code</Label>
                <Input value={ifscCode} onChange={(e) => setIfscCode(e.target.value)} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Bank Name</Label>
                <Input value={bankName} onChange={(e) => setBankName(e.target.value)} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Address Line 1</Label>
                <Input value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={addrCity} onChange={(e) => setAddrCity(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={addrState} onChange={(e) => setAddrState(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input value={addrCountry} onChange={(e) => setAddrCountry(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Postal Code</Label>
                <Input value={addrPostal} onChange={(e) => setAddrPostal(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setBenDialogOpen(false)} disabled={loading}>Cancel</Button>
              <Button onClick={async () => { await onSaveBeneficiary(); setBenDialogOpen(false); }} disabled={loading || !benName || !accountNumber || !ifscCode || !bankName}>Save Beneficiary</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payout Detail Modal */}
      <PayoutDetailModal
        open={payoutDetailModalOpen}
        onOpenChange={setPayoutDetailModalOpen}
        payout={selectedPayout}
        onActionComplete={() => {
          setPayoutDetailModalOpen(false);
          // Refresh payouts list if needed
        }}
        userRole={user?.role}
      />
    </div>
  );
}
